import React, { useEffect, useState } from 'react';
import {compose} from 'recompose';
import { withRouter, Link } from "react-router-dom";
import { styled } from '@mui/material/styles';

import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import AppsIcon from '@mui/icons-material/Apps';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import HomeWorkIcon from '@mui/icons-material/HomeWork';

import { userActions } from '../_utils';

import logo from '../_img/logo.svg';

export const drawerWidth = 214;

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: 'border-box',
    backgroundColor: '#0B2033',
    color: '#C3D3E0',
    borderRight: 0,
  },
});

const workItems = [
    {
        to: '/',
        title: 'Главная',
        icon: <AppsIcon />
    },
    {
        to: '/schedule',
        title: 'Расписание',
        permissions: ['admin', 'supervisor'],
        icon: <EventNoteIcon />
    },
    {
        to: '/visor',
        title: 'Прослушка',
        permissions: ['admin', 'supervisor'],
        icon: <VolumeUpIcon />
    },
    {
        to: '/statistics',
        title: 'Статистика',
        permissions: ['admin', 'supervisor'],
        icon: <BarChartIcon />
    },
    {
        to: '/reports',
        title: 'Отчеты',
        permissions: ['admin', 'supervisor'],
        icon: <AssignmentIcon />
    },
];

const setupItems = [
    {
        to: '/projects',
        title: 'Проекты',
        permissions: ['admin', 'supervisor'],
        icon: <BusinessCenterIcon />
    },
    {
        to: '/queues',
        title: 'Очереди',
        permissions: ['admin'],
        icon: <SwapHorizIcon />
    },
    {
        to: '/scripts',
        title: 'Скрипты',
        permissions: ['admin', 'supervisor'],
        icon: <AccountTreeIcon />
    },
    {
        to: '/operators',
        title: 'Сотрудники',
        permissions: ['admin'],
        icon: <RecentActorsIcon />
    },
];

const secondaryListItems = [
    {
        to: '/users',
        title: 'Пользователи',
        permissions: ['admin'],
        icon: <PeopleAltIcon />
    },
    {
        to: '/cabinets',
        title: 'Кабинеты',
        permissions: ['admin'],
        icon: <HomeWorkIcon />
    },
    {
        to: '/login',
        title: 'Выход',
        onClick: userActions.logout,
        icon: <ExitToAppIcon />
    },
];

export const MenuItems = [
    ...workItems,
    ...setupItems,
    ...secondaryListItems,
];

const navButtonSx = {
    color: '#B6C9D9',
    height: 32,
    borderRadius: 0,
    px: 2,
    position: 'relative',
    '& .MuiListItemIcon-root': {
        minWidth: 26,
        color: 'inherit',
        opacity: 0.75,
        '& svg': { fontSize: 15 },
    },
    '& .MuiListItemText-primary': {
        fontSize: 12.5,
        color: 'inherit',
    },
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,.05)',
        color: '#fff',
    },
    '&.Mui-selected': {
        color: '#fff',
        backgroundColor: 'rgba(0,137,184,.18)',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 4,
            bottom: 4,
            width: 2,
            borderRadius: '0 2px 2px 0',
            backgroundColor: '#9DD3E8',
        },
        '& .MuiListItemIcon-root': {
            opacity: 1,
            color: '#9DD3E8',
        },
    },
    '&.Mui-selected:hover': {
        backgroundColor: 'rgba(0,137,184,.22)',
        color: '#fff',
    },
};

const NavGroup = ({ title, items, pathname, onSelect }) => (
    <Box sx={{ pt: 1.75, pb: 0.5 }}>
        {title && (
            <Typography
                sx={{
                    fontFamily: 'var(--mono)',
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: '#5F7C96',
                    px: 2,
                    pb: 0.75,
                }}
            >
                {title}
            </Typography>
        )}
        <List dense disablePadding>
            {items.map((item, index) => {
                const mainPath = item.to.split('/');
                const selected = pathname[1] === mainPath[1];
                return (
                    <ListItem
                        key={`${item.to}-${index}`}
                        disablePadding
                        sx={{ display: 'block' }}
                        component={item.onClick ? 'div' : Link}
                        to={item.onClick ? undefined : item.to}
                        onClick={item.onClick || onSelect}
                    >
                        <ListItemButton selected={selected} sx={navButtonSx}>
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.title} />
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>
    </Box>
);

const Menu = ({ location, onSelect, open }) => {
    const [pathname, setPathname] = useState([]);

    useEffect(() => {
        if (location.pathname) {
            const nextPath = location.pathname.split('/');
            setPathname(nextPath);
            const item = MenuItems.find((m) => {
                const mainPath = m.to.split('/');
                return nextPath[1] === mainPath[1];
            });
            document.title = item ? `Profcontact | ${item.title}` : 'Profcontact';
        }
    }, [location]);

    return (
        <Drawer
            variant="permanent"
            sx={{
                display: { xs: open ? 'block' : 'none', md: 'block' },
            }}
        >
            <Box
                component={Link}
                to="/"
                sx={{
                    height: 52,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.75,
                    borderBottom: '1px solid rgba(255,255,255,.07)',
                    flexShrink: 0,
                    textDecoration: 'none',
                }}
            >
                <Box
                    component="img"
                    src={logo}
                    alt="ProfContact"
                    sx={{
                        height: 38,
                        width: 'auto',
                        maxWidth: 178,
                        objectFit: 'contain',
                        objectPosition: 'left center',
                        display: 'block',
                    }}
                />
            </Box>
            <Box
                sx={{
                    overflow: 'auto',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    <NavGroup title="Работа" items={workItems} pathname={pathname} onSelect={onSelect} />
                    <NavGroup title="Настройка" items={setupItems} pathname={pathname} onSelect={onSelect} />
                </Box>
                <Box sx={{ borderTop: '1px solid rgba(255,255,255,.07)', py: 1, pb: 1.5 }}>
                    <NavGroup items={secondaryListItems} pathname={pathname} onSelect={onSelect} />
                </Box>
            </Box>
        </Drawer>
    );
}

export default compose(withRouter)(Menu);
